import { useEffect, useRef, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2, Lock } from "lucide-react";
import { AccountPage, validateTokenSearch } from "@/components/auth/AccountPage";
import { accountAPI } from "@/lib/accountApi";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/konto/passwort")({
  validateSearch: validateTokenSearch,
  component: ResetPasswordPage,
});

const MIN_PASSWORD_LENGTH = 8;

const inputClass =
  "w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60";

function ResetPasswordPage() {
  const { token: tokenFromUrl } = Route.useSearch();
  const navigate = useNavigate();
  const { openAuthModal, subscribe } = useAuth();
  // Kept here, because the address bar drops it below.
  const tokenRef = useRef(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Keep the token out of the browser history.
  useEffect(() => {
    if (tokenFromUrl) {
      tokenRef.current = tokenFromUrl;
      void navigate({ to: "/konto/passwort", search: {}, replace: true });
    }
  }, [tokenFromUrl, navigate]);

  useEffect(
    () =>
      subscribe((change) => {
        if (change === "login") void navigate({ to: "/" });
      }),
    [subscribe, navigate]
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }
    if (password !== repeat) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    const token = tokenRef.current;
    if (!token) return;

    setIsSubmitting(true);
    const result = await accountAPI.confirmPasswordReset(token, password);
    setIsSubmitting(false);
    if (result.ok) {
      setDone(true);
      setPassword("");
      setRepeat("");
    } else {
      setError(result.detail);
    }
  };

  if (!tokenRef.current) {
    return (
      <AccountPage title="Neues Passwort">
        <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4">
          <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
          <p className="text-sm text-red-700">
            Der Link ist unvollständig. Bitte öffnen Sie ihn erneut aus der E-Mail oder fordern
            Sie über "Passwort vergessen?" einen neuen an.
          </p>
        </div>
      </AccountPage>
    );
  }

  if (done) {
    return (
      <AccountPage title="Neues Passwort">
        <div className="space-y-6">
          <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 p-4">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <p className="font-medium text-green-800">Passwort geändert</p>
              <p className="text-sm text-green-700 mt-1">
                Sie sind jetzt auf allen Geräten abgemeldet. Melden Sie sich mit dem neuen
                Passwort an.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openAuthModal("login")}
            className="px-6 py-2.5 text-sm font-medium rounded-lg text-white shadow-xs bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:shadow-md transition-all"
          >
            Anmelden
          </button>
        </div>
      </AccountPage>
    );
  }

  return (
    <AccountPage title="Neues Passwort">
      <p className="text-gray-600 mb-6">Legen Sie ein neues Passwort für Ihr Konto fest.</p>

      {error && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
        >
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</span>
          <span className="relative block">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={MIN_PASSWORD_LENGTH}
              required
              disabled={isSubmitting}
              className={inputClass}
              placeholder="••••••••"
            />
          </span>
          <span className="block text-xs text-gray-500 mt-1">
            Mindestens {MIN_PASSWORD_LENGTH} Zeichen
          </span>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Passwort wiederholen
          </span>
          <span className="relative block">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              required
              disabled={isSubmitting}
              className={inputClass}
              placeholder="••••••••"
            />
          </span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            "flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg text-white shadow-xs transition-all",
            isSubmitting
              ? "bg-primary-400 cursor-wait"
              : "bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:shadow-md"
          )}
        >
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          <span>Passwort speichern</span>
        </button>
      </form>
    </AccountPage>
  );
}
